import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { listGoogleDriveFiles, type DriveFile } from '../lib/googleDrive';
import { signInWithGoogle, supabaseEnabled } from '../lib/supabase';
import { RefreshCw, ExternalLink, HardDrive, FileText, Search } from 'lucide-react';

interface Props {
  onClose(): void;
}

export default function SavedDriveFilesModal({ onClose }: Props) {
  const [files, setFiles] = useState<DriveFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchFiles = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await listGoogleDriveFiles();
      if (result.success && result.files) {
        setFiles(result.files);
      } else {
        setError(result.error || 'Failed to fetch files.');
      }
    } catch (err: any) {
      setError(err?.message || 'An unexpected error occurred.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFiles();
  }, []);

  function formatBytes(bytes?: string | number) {
    if (!bytes) return 'N/A';
    const num = typeof bytes === 'string' ? parseInt(bytes, 10) : bytes;
    if (isNaN(num)) return 'N/A';
    if (num === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(num) / Math.log(k));
    return parseFloat((num / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  const filteredFiles = files.filter(f =>
    f.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="modal-backdrop"
      onClick={onClose}
      style={{ zIndex: 9999 }}
    >
      <motion.div
        initial={{ opacity: 0, y: 15, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 10, scale: 0.95 }}
        transition={{ type: "spring", damping: 30, stiffness: 350 }}
        className="modal glass glass--strong"
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: '600px', width: '90%', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
          <h2 className="sc" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <HardDrive size={16} /> Google Drive Assets
          </h2>
          <button className="btn btn--ghost text-xs" onClick={onClose} style={{ padding: '2px 8px', minHeight: 'auto' }}>
            close
          </button>
        </div>

        <p style={{ fontSize: 11, color: 'var(--fg-dim)', marginBottom: '14px' }}>
          Assets generated in ProtoLive Studio and saved directly to your Google Drive sandbox.
        </p>

        {error ? (
          <div className="col" style={{ gap: '12px', alignItems: 'center', padding: '30px 10px', textAlign: 'center' }}>
            <span style={{ fontSize: '12px', color: 'var(--accent-red, #ff5e5e)' }}>{error}</span>
            {error.includes('Sign in') && supabaseEnabled && (
              <button 
                className="btn btn--primary" 
                onClick={() => signInWithGoogle()}
                style={{ fontSize: '11px', padding: '6px 12px' }}
              >
                Sign in with Google
              </button>
            )}
            {!error.includes('Sign in') && (
              <button className="btn btn--ghost" onClick={fetchFiles} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <RefreshCw size={12} /> Retry
              </button>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--fg-dim)', opacity: 0.6 }} />
                <input
                  type="text"
                  placeholder="Search saved files..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  style={{ width: '100%', padding: '6px 10px 6px 30px', fontSize: '12px', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(0,0,0,0.2)', color: 'var(--fg)' }}
                />
              </div>
              <button 
                className="btn btn--ghost" 
                onClick={fetchFiles} 
                disabled={loading}
                style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '0 8px', minHeight: 'auto' }}
                title="Refresh Drive index"
              >
                <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
                <span style={{ fontSize: '10px' }}>Refresh</span>
              </button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '6px', background: 'rgba(0,0,0,0.1)' }}>
              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px' }}>
                  <RefreshCw className="animate-spin text-purple-400" size={24} />
                </div>
              ) : filteredFiles.length === 0 ? (
                <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--fg-dim)', fontSize: '12px' }}>
                  {searchQuery ? 'No matching files found.' : 'No saved files found in your Google Drive.'}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  {filteredFiles.map(file => (
                    <div 
                      key={file.id} 
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between', 
                        padding: '10px 12px', 
                        borderBottom: '1px solid rgba(255,255,255,0.05)',
                        transition: 'background 0.2s'
                      }}
                      className="hover:bg-white/5"
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                        <FileText size={16} className="text-purple-400 shrink-0" />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: '12px', fontWeight: '500', color: 'var(--fg)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={file.name}>
                            {file.name}
                          </div>
                          <div style={{ fontSize: '10px', color: 'var(--fg-dim)', display: 'flex', gap: '8px' }}>
                            <span>Size: {formatBytes(file.size)}</span>
                            <span>•</span>
                            <span>{file.createdTime ? new Date(file.createdTime).toLocaleDateString() : 'N/A'}</span>
                          </div>
                        </div>
                      </div>

                      {file.webViewLink && (
                        <a 
                          href={file.webViewLink} 
                          target="_blank" 
                          rel="noreferrer" 
                          className="btn btn--primary btn--icon"
                          style={{ width: '26px', height: '26px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          title="Open in Google Drive"
                        >
                          <ExternalLink size={12} />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <hr className="divider" style={{ margin: '14px 0' }} />
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn--ghost" onClick={onClose} style={{ fontSize: '11px' }}>
            Close
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
